import React from "react";
import {
  FaHome,
  FaBullseye,
  FaMapMarkedAlt,
  FaRegBuilding,
  FaCity,
  FaRegFileAlt,
  FaCalculator,
  FaEnvelopeOpenText,
  FaRegAddressCard,
  FaUser,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
} from "react-icons/fa";
import logo from "../assets/logo.png";

/**
 * Sidebar del usuario Comercial (typeuser 4). Mismo look que SidebarEmployee
 * (barra azul fija, colapsable) pero acotado a las pantallas del comercial:
 * Inicio + Prospección + Mi Perfil. No depende de edificio.
 */
const SidebarComercial = ({ selected, setSelected, collapsed = false, setCollapsed }) => {
  const menuSections = [
    {
      title: "GENERAL",
      items: [
        { id: "homecomercial", icon: FaHome, label: "Inicio" },
        { id: "prospeccion", icon: FaBullseye, label: "Prospección" },
        { id: "mapaprospeccion", icon: FaMapMarkedAlt, label: "Mapa" },
        { id: "desarrolladoras", icon: FaRegBuilding, label: "Desarrolladoras" },
        { id: "administraciones", icon: FaCity, label: "Administraciones" },
        { id: "contactoscomercial", icon: FaRegAddressCard, label: "Contactos" },
        { id: "materiales", icon: FaRegFileAlt, label: "Materiales" },
        { id: "cotizador", icon: FaCalculator, label: "Cotizador" },
        { id: "presupuestoexpress", icon: FaEnvelopeOpenText, label: "Presupuesto exprés" },
      ],
    },
    {
      title: "PERSONAL",
      items: [
        { id: "perfil", icon: FaUser, label: "Mi Perfil" },
      ],
    },
  ];

  const handleToggle = () => {
    if (setCollapsed) setCollapsed(!collapsed);
  };

  return (
    <aside
      style={{ ...styles.sidebar, width: collapsed ? 64 : 220, minWidth: collapsed ? 64 : 220 }}
      role="navigation"
      aria-label="Menú principal comercial"
    >
      <div style={{
        ...styles.header,
        justifyContent: collapsed ? "center" : "space-between",
        padding: collapsed ? "12px 6px" : "12px 10px",
      }}>
        {!collapsed && (
          <div
            style={styles.logoContainer}
            onClick={() => setSelected("homecomercial")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && setSelected("homecomercial")}
            aria-label="Ir al inicio"
          >
            <img src={logo} alt="Habitia - Ir al inicio" style={{ ...styles.logo, height: 48, filter: "brightness(0) invert(1)" }} />
            {process.env.REACT_APP_FIREBASE_PROJECT_ID === "habitia-dev-8b512" && (
              <span style={{
                display: "block",
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.6)",
                marginTop: 2,
                lineHeight: 1,
              }}>Testing</span>
            )}
          </div>
        )}
        <button style={styles.toggleBtn} onClick={handleToggle} aria-label={collapsed ? "Expandir menú" : "Minimizar menú"}>
          {collapsed ? <FaAngleDoubleRight size={14} color="#ffffff" /> : <FaAngleDoubleLeft size={14} color="#ffffff" />}
        </button>
      </div>

      <nav style={styles.nav}>
        {menuSections.map((section, sectionIndex) => (
          <div key={section.title} style={styles.section}>
            {!collapsed && <div style={styles.sectionTitle}>{section.title}</div>}
            {collapsed && sectionIndex > 0 && <div style={styles.sectionDivider}></div>}
            {section.items.map((item) => {
              const isActive = selected === item.id;
              return (
                <div
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  aria-current={isActive ? "page" : undefined}
                  style={{
                    ...styles.menuItem,
                    ...(isActive ? styles.menuItemActive : {}),
                    justifyContent: collapsed ? "center" : "flex-start",
                    padding: collapsed ? "8px" : "8px 10px",
                  }}
                  onClick={() => setSelected(item.id)}
                  onKeyDown={(e) => e.key === "Enter" && setSelected(item.id)}
                  onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)"; }}
                  onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = "transparent"; }}
                  title={collapsed ? item.label : ""}
                  aria-label={collapsed ? item.label : undefined}
                >
                  <item.icon size={18} color={isActive ? "#0080ff" : "rgba(255,255,255,0.85)"} style={{ flexShrink: 0 }} />
                  {!collapsed && (
                    <span style={{ ...styles.menuLabel, color: isActive ? "#0080ff" : "#ffffff", fontWeight: 400 }}>
                      {item.label}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </nav>
    </aside>
  );
};

const styles = {
  sidebar: {
    position: "fixed", left: 0, top: 0, bottom: 0, backgroundColor: "#0080ff",
    display: "flex", flexDirection: "column", height: "100vh", minHeight: "100vh",
    flexShrink: 0, transition: "width 0.2s ease, min-width 0.2s ease", overflow: "hidden",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif", zIndex: 100,
  },
  header: { display: "flex", alignItems: "center", borderBottom: "1px solid rgba(255,255,255,0.2)" },
  logoContainer: { display: "flex", alignItems: "center", cursor: "pointer", flex: 1 },
  logo: { width: "auto", objectFit: "contain" },
  toggleBtn: {
    width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.15)", border: "none", borderRadius: 6, cursor: "pointer",
    transition: "all 0.15s ease", flexShrink: 0,
  },
  nav: { flex: 1, overflowY: "auto", padding: "8px 6px", display: "flex", flexDirection: "column", gap: 2, minHeight: 0 },
  section: { marginBottom: 4 },
  sectionTitle: { fontSize: 10, fontWeight: 400, color: "rgba(255,255,255,0.65)", letterSpacing: "0.5px", padding: "4px 10px 2px", textTransform: "uppercase" },
  sectionDivider: { height: 1, backgroundColor: "rgba(255,255,255,0.2)", margin: "4px 10px" },
  menuItem: { display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8, cursor: "pointer", transition: "all 0.15s ease", position: "relative" },
  menuItemActive: { backgroundColor: "#ffffff", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
  menuLabel: { fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1 },
};

export default SidebarComercial;
